import { Form, FormField, Input, MessageBox, ModalDialog } from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { PendingUser } from "../../http/types/pendingUsers";
import { usePendingUsersApi } from "../../http/usePendingUsers";
import { useUsersApi } from "../../http/useUsers";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import RolesPermissionsFields from "./RolesPermissionsFields";

type EmailFormBody = {
  email: string | null;
};

type PendingUserModalProps = {
  // the pending user edited: none adds a user, stored right away (and opened) when the email has an account
  pendingUser?: PendingUser;
  onClose: () => void;
};

const PendingUserModal: FC<PendingUserModalProps> = ({ pendingUser, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.users;
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const usersApi = useUsersApi();
  const pendingUsersApi = usePendingUsersApi();
  const [roles, setRoles] = useState(pendingUser?.roles ?? []);
  const [permissions, setPermissions] = useState(pendingUser?.permissions ?? []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = () => async (formData: EmailFormBody) => {
    if (!pendingUser && !formData.email) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (pendingUser) {
        await pendingUsersApi.updatePendingUser(pendingUser.id, { roles, permissions });
      } else {
        const result = await usersApi.addUser({ email: formData.email!, roles, permissions });
        if (result.userId) {
          navigate(`/admin/users/${result.userId}`);
        }
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalDialog onClose={onClose} title={pendingUser ? t.pending.edit : t.add}>
      <Form<EmailFormBody>
        submitText={i18n.translation.common.save}
        disabled={isSubmitting}
        defaultValues={{ email: pendingUser?.email ?? null }}
        onSubmit={onSubmit}
      >
        {({ register, formState: { errors } }) => (
          <FormFieldsContainer fullWidth>
            {!pendingUser && <MessageBox type="informative" content={t.pending.info} />}
            <FormField label={i18n.translation.common.email} error={errors.email}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={!!pendingUser || isSubmitting}
                  {...register("email", { required: !isOptional, minLength: 1 })}
                />
              )}
            </FormField>
            <RolesPermissionsFields
              roles={roles}
              permissions={permissions}
              onRolesChange={setRoles}
              onPermissionsChange={setPermissions}
              disabled={isSubmitting}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default PendingUserModal;
